import { Controller } from "@hotwired/stimulus"

// The hosted survey page (survey_responses/_form), one question at a time like the chat widget: a progress bar,
// Back / Next, and Submit on the last question. A single pick moves on by itself; text and multiple choice wait for
// Next. Each answer is saved as it's given (a JSON PATCH, like the widget's per-step saves), so a survey left half-way
// still counts; Submit then finishes it. A follow-up is only asked while the earlier answer it depends on matches its
// condition — the hosted-page twin of SurveyQuestion#visible_for? (and the widget's survey-conditions.ts). Conditions
// only point at earlier questions, so walking them in order skips a follow-up of a skipped question too.
// With `prefilled` (an answer picked in the survey email), that answer is saved as soon as the page opens and the page
// opens on the next question; with `preview` (the merchant's), nothing is saved.
export default class extends Controller {
  static targets = ['question', 'progress', 'progressLabel', 'progressBar', 'back', 'next', 'submit']
  // focus: in the merchant's preview, the question the survey builder is editing, shown even if it's a follow-up.
  static values = { prefilled: Boolean, preview: Boolean, progress: String, focus: String }

  connect() {
    this.stepping = true
    // The page rendered on its starting question (survey_start); from here the controller shows and hides.
    this.element.querySelectorAll('.survey-later').forEach((element) => element.classList.remove('survey-later'))
    // Opens on the first question still to answer: the one after an answer picked in the email, or where a Submit
    // that couldn't finish left off.
    const asked = this.askedQuestions()
    this.currentKey = this.focusValue ||
      (asked.find((question) => this.answerOf(question) === null) || asked[asked.length - 1])?.dataset.key
    this.refresh()
    if (this.prefilledValue) this.save(this.answers())
  }

  // A pick is saved at once; typing is saved once the customer pauses. A multiple choice question saves all its picks.
  change(event) {
    this.refresh()
    const input = event.target
    const key = input.name.match(/^answers\[([^\]]+)\]/)?.[1]
    if (!key) return

    clearTimeout(this.typingTimer)
    const question = this.questionTargets.find((q) => q.dataset.key === key)
    const save = () => this.save({ [key]: question ? this.answerOf(question) ?? '' : input.value })
    if (input.tagName === 'TEXTAREA') {
      this.typingTimer = setTimeout(save, 600)
      return
    }

    save()
    if (question?.dataset.picksItself === 'true' && !this.isLast()) {
      clearTimeout(this.advanceTimer)
      this.advanceTimer = setTimeout(() => this.next(), 350)
    }
  }

  next() {
    clearTimeout(this.advanceTimer)
    const asked = this.askedQuestions()
    const index = asked.findIndex((question) => question.dataset.key === this.currentKey)
    if (!this.canContinue(asked[index]) || index >= asked.length - 1) return

    this.currentKey = asked[index + 1].dataset.key
    this.refresh()
  }

  back() {
    clearTimeout(this.advanceTimer)
    const asked = this.askedQuestions()
    const index = asked.findIndex((question) => question.dataset.key === this.currentKey)
    if (index > 0) this.currentKey = asked[index - 1].dataset.key
    this.revisitedKey = this.currentKey
    this.refresh()
  }

  refresh() {
    const asked = this.askedQuestions()
    this.questionTargets.forEach((question) => { question.disabled = !asked.includes(question) })
    if (!this.stepping) return

    if (!asked.some((question) => question.dataset.key === this.currentKey)) this.currentKey = asked[0]?.dataset.key
    const index = asked.findIndex((question) => question.dataset.key === this.currentKey)
    const current = asked[index]
    this.questionTargets.forEach((question) => { question.hidden = question !== current })
    if (current && current !== this.shownQuestion && this.shownQuestion && typeof current.animate === 'function') {
      current.animate([{ opacity: 0, transform: 'translateY(8px)' }, { opacity: 1, transform: 'none' }],
        { duration: 220, easing: 'ease-out' })
    }
    this.shownQuestion = current

    const last = index === asked.length - 1
    // A class, not `hidden`: the buttons' own display would win over the attribute.
    this.backTarget.classList.toggle('hidden', index <= 0)
    // A required single-pick question moves on by itself, so it only offers Next when the customer came back to it
    // answered. An optional one keeps Next, to skip it.
    const revisited = this.revisitedKey === this.currentKey && this.answerOf(current) !== null
    const picksItself = current?.dataset.picksItself === 'true' && current.dataset.required === 'true' && !revisited
    this.nextTarget.classList.toggle('hidden', last || Boolean(picksItself))
    this.nextTarget.disabled = !this.canContinue(current)
    this.submitTarget.classList.toggle('hidden', !last)
    this.progressLabelTarget.textContent = this.progressValue
      .replace('__CURRENT__', index + 1).replace('__TOTAL__', asked.length)
    this.progressBarTarget.style.width = `${((index + 1) / asked.length) * 100}%`
  }

  isLast() {
    const asked = this.askedQuestions()
    return asked[asked.length - 1]?.dataset.key === this.currentKey
  }

  canContinue(question) {
    return Boolean(question) && (question.dataset.required !== 'true' || this.answerOf(question) !== null)
  }

  // The questions asked given the answers so far, in order (in the builder's preview, the one it's editing too).
  askedQuestions() {
    const answers = {}
    return this.questionTargets.filter((question) => {
      const asked = this.holds(question.dataset.condition, answers) ||
        (this.focusValue !== '' && question.dataset.key === this.focusValue)
      const answer = asked ? this.answerOf(question) : null
      if (answer !== null) answers[question.dataset.key] = answer
      return asked
    })
  }

  answers() {
    return Object.fromEntries(
      this.askedQuestions().map((q) => [q.dataset.key, this.answerOf(q)]).filter(([, a]) => a !== null)
    )
  }

  // Saves run one after another, so an earlier one finishing late can't overwrite a newer answer.
  save(answers) {
    if (this.previewValue) return // the merchant's preview saves nothing

    const token = document.querySelector('meta[name="csrf-token"]')?.content
    const send = () => fetch(this.element.action, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-CSRF-Token': token },
      body: JSON.stringify({ answers }),
    }).catch(() => {}) // Submit saves everything again, and says what's missing.
    // Sent at once when nothing else is on its way, else after the one before it.
    this.inFlight = (this.inFlight || 0) + 1
    const run = () => send().finally(() => { this.inFlight -= 1 })
    this.saving = this.inFlight > 1 ? this.saving.then(run) : run()
    return this.saving
  }

  // Submit waits for the saves still on their way, so none lands after it.
  finish(event) {
    if (!this.inFlight) return

    event.preventDefault()
    if (this.finishing) return // a second click while waiting

    this.finishing = true
    this.saving.then(() => this.element.submit())
  }

  holds(conditionJson, answers) {
    if (!conditionJson) return true

    const condition = JSON.parse(conditionJson)
    const answer = answers[condition.question_key]
    if (answer === undefined) return false

    switch (condition.operator) {
      case 'lte': return Number(answer) <= Number(condition.value)
      case 'gte': return Number(answer) >= Number(condition.value)
      case 'equals': return answer === String(condition.value)
      case 'in': return [answer].flat().some((pick) => (condition.value || []).map(String).includes(pick))
      default: return false
    }
  }

  answerOf(question) {
    const boxes = question.querySelectorAll('input[type=checkbox]')
    if (boxes.length) {
      const picks = [...boxes].filter((box) => box.checked).map((box) => box.value)
      return picks.length ? picks : null
    }

    const checked = question.querySelector('input:checked')
    if (checked) return checked.value

    const text = question.querySelector('textarea')
    return text && text.value.trim() !== '' ? text.value : null
  }
}
